import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Stack from "@mui/material/Stack"
import { styled } from "@mui/material/styles"
import { searchUser } from "utils/componentsUtils"
import { Size } from "types/general.types"
import { SMALL } from "constants/index"
import SearchInput from "components/atoms/SearchInput"
import UserCard from "components/molecules/UserCard"
import ExploreEmptyState from "components/molecules/ExploreEmptyState"
import LoadingPlaceholder from "components/organisms/UsersList/LoadingPlaceholder"
import FadeInAnimation from "components/animations/FadeInAnimation"
import ActionButtons from "components/organisms/UsersList/ActionButtons"

type UserSearchResultProps = {
  users: UserDto[] | UserFollowDto[] | null;
  loading: boolean;
  defaultSearchValue?: string;
  clientSideSearch?: boolean;
  showFollowButton?: boolean;
  showRemoveFollowerButton?: boolean;
  emptySearchStatePlaceholder?: React.ReactNode;
  searchInputSize?: Size;
  extraActionButton?: React.ReactNode | null;
  badge?: React.ReactNode | null;
  showBadge?: (value: any) => boolean;
  style?: React.CSSProperties;
  onSearch?: (searchedValue: string) => void;
  onFollowerRemove?: (id: number) => void;
  onClick?: (user: UserFollowDto) => void;
}

export default function UsersList(props: UserSearchResultProps) {
  const {
    users,
    loading,
    defaultSearchValue = "",
    clientSideSearch = true,
    showFollowButton = false,
    showRemoveFollowerButton = false,
    emptySearchStatePlaceholder = null,
    searchInputSize = SMALL,
    extraActionButton = null,
    badge = null,
    showBadge,
    style = {},
    onSearch,
    onFollowerRemove,
    onClick = new Function()
  } = props

  const [localUsers, setLocalUsers] = useState<UserDto[] | UserFollowDto[] | null>(null)

  const { t } = useTranslation()

  useEffect(() => {
    setLocalUsers(structuredClone(users))
  }, [users])

  function handleSearchUser(event: React.ChangeEvent<HTMLInputElement>) {
    const searchedValue = event.target.value

    if (clientSideSearch) {
      const searchedValue = event.target.value
      const filteredUsers = searchUser(users, searchedValue)
      setLocalUsers(filteredUsers)
    }

    if (typeof onSearch === "function") {
      onSearch(searchedValue)
    }
  }

  return (
    <Container style={style}>
      <Stack direction="row" alignItems="center" justifyContent="center" gap="12px">
        <SearchInput
          id={"search-user"}
          defaultValue={defaultSearchValue}
          placeholder={t("general.searchUser")}
          size={searchInputSize}
          onChange={handleSearchUser}
        />
        {extraActionButton}
      </Stack>
      {!loading ? <Stack
        height={searchInputSize === SMALL ? "calc(100% - 50px)" : "calc(100% - 58px)"}
        overflow="scroll"
        gap="8px"
        padding="0 8px 8px"
        flexGrow={1}
        flexShrink={0}
      >
        {(defaultSearchValue.length || !emptySearchStatePlaceholder) ? <>
          {(!localUsers || localUsers?.length) ? localUsers?.map((user, index) => (
            <FadeInAnimation key={user?.id}>
              <UserRow>
                <UserCard
                  id={user?.id}
                  handle={user?.handle}
                  name={user?.name || ""}
                  avatar={user?.avatar}
                  avatarWidth={"46px"}
                  badge={(showBadge && showBadge(user)) ? badge : null}
                  onClick={() => onClick(user)}
                />
                {user.hasOwnProperty("followStatus") && <ActionButtons
                    user={user as UserFollowDto}
                    showFollowButton={showFollowButton}
                    showRemoveFollowerButton={showRemoveFollowerButton}
                    onFollowerRemove={onFollowerRemove}
                />}
              </UserRow>
            </FadeInAnimation>
          )) : <ExploreEmptyState/>}
        </> : emptySearchStatePlaceholder}
      </Stack> : <LoadingPlaceholder/>}
    </Container>
  )
}

const Container = styled(Stack)(({ theme }) => ({
  height: "100%",
  flexGrow: 1,
  gap: "8px",
  overflow: "hidden",

  "& .MuiTextField-root": {
    marginBottom: "0 !important"
  }
}))

const UserRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  flexShrink: 0,

  "& svg": {
    width: "16px",
    height: "16px"
  },

  "& path": {
    fill: theme?.palette?.gray["14"]
  }
}))
